import { FirebaseError } from 'firebase/app'
import { httpsCallable } from 'firebase/functions'
import { deleteField } from 'firebase/firestore'
import { DateTime } from 'luxon'
import {
  action,
  computed,
  type IObservableValue,
  makeObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { assert } from '../firestore-mobx/utils'
import type { RoomStateVideoMethod } from '../firestore/RoomState/types'
import {
  createRoomState,
  forceAIProcessing,
  getRoomState,
  getRoomStates,
  getRoomWasGraded,
  getStudentWasGraded,
  leaveRoom,
  roomStateAddUser,
  roomStateReset,
  updateRoomState,
} from '../firestore/RoomState'
import {
  getRoomStateAnswers,
  submitUserAnswer,
  upsertUserAnswer,
} from '../firestore/RoomStateAnswer'
import { getSection } from '../firestore/Section'
import {
  fetchSectionAssignments,
  getSectionAssignment,
} from '../firestore/SectionAssignment'
import { fetchSlideDeck, fetchSlideDecksById } from '../firestore/SlideDeck'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { RoomState } from '../models/RoomState'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { Section } from '../models/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideQuestion } from '../models/SlideQuestion'
import { buildShuffledAnswers, type ShuffledAnswer } from '../util/shuffle'
import { ValidLibraryObject } from '../stores/ValidLibraryObject'
import {
  LibraryObjectState,
  isFeaturedImageType,
  SlideDeckMaterialWorkType,
} from '../types'
import { Cubit } from './core'
import { ManagedDialogState } from './shared/ManagedDialogState'
import { getRoomStateSummary } from '../firestore/RoomStateSummary'
import { RoomStateSummary } from '../models/RoomStateSummary'
import { captureException } from '@sentry/core'
import { SlideModel } from '../models/SlideModel'
import { populateModelsFromApiResponse } from '../firestore-mobx/apiModelHelper'
import type { SlideDeckInclude } from '../types/slideDeckAccess'
import { calculateTimeLeftInSession } from '../util/time'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { UserProfileSectionPass } from '../models/UserProfileSectionPass'
import { getSectionPassForSection } from '../firestore/UserProfileSectionPass'
import { getQuizAnswerScore } from '../sources-of-truth/scoring'
import { generateUniqueGroupNames } from '../sources-of-truth/defaultGroupNames'
import { StudentAssignmentChatController } from './StudentAssignment/StudentAssignmentChatController'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { getSectionPassPriceAfterRebate } from '../sources-of-truth/sectionPassPrice'
import { StudentPeerReviewController } from './StudentAssignment/StudentPeerReviewController'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { getRoleIssueForRoomState } from '../sources-of-truth/getRoleIssueForRoomState'
import { getMinGroupSizeIssue } from '../sources-of-truth/getMinGroupSizeIssue'

class UnauthorizedStudentAssignmentAccessError extends Error {
  name = 'UnauthorizedStudentAssignmentAccessError'
}

export class StripeCheckoutError extends Error {
  name = 'StripeCheckoutError'
}

// Temporary stopgap for a production retry loop caused by client/server access
// state disagreement. Remove once the underlying state mismatch is resolved.
const MAX_SLIDE_DECK_ACCESS_RETRIES = 3

export class StudentAssignmentCubit extends Cubit {
  repository: FirebaseRepository
  user: BreakoutUser

  // Unique per cubit instance. Forwarded on getSlideDeckForAssignment calls so
  // the server logs can distinguish a re-created cubit (a fresh id on every
  // call = a remount/recreate loop) from a stable one re-fetching.
  readonly instanceId = crypto.randomUUID().slice(0, 8)

  sectionId: string
  assignmentId: string
  @observable roomStateId?: string
  // The URL points at a room the student can't view (a foreign/stale room, or
  // one they were removed from). We deliberately do NOT clear roomStateId and
  // redirect in that case — that clear->redirect->re-push cycle was the
  // getSlideDeckForAssignment remount ring. Instead we surface a no-access pane
  // and let the student navigate away with a click. Reset on any deliberate
  // room change (see changeRoomStateId).
  @observable roomAccessDenied = false

  section: Section
  assignment: SectionAssignment
  slideDeck: SlideDeck
  roomState: RoomState
  roomStates: StaticModelCollection<RoomState>
  roomStateSummary: RoomStateSummary
  slides: StaticModelCollection<SlideModel>
  sectionPassPricing: SettingsSectionPassPricing
  sectionPass: UserProfileSectionPass

  questions: StaticModelCollection<SlideQuestion>
  answers: StaticModelCollection<RoomStateAnswer>
  roles: StaticModelCollection<SlideDeckRole>
  private _roomGraded: IObservableValue<null | boolean> = observable.box(null)
  private _materials: StaticModelCollection<SlideDeckMaterial>
  private _assignments: StaticModelCollection<SectionAssignment>
  private _slideDecks: StaticModelCollection<SlideDeck>
  private chatController?: StudentAssignmentChatController
  private _peerReviewController?: StudentPeerReviewController
  private _studentGraded: IObservableValue<null | boolean> =
    observable.box(null)

  private _roomStateUserIds = observable.array<string>([])

  // Guards initializeOtherStreams so it runs exactly once per cubit lifetime.
  private _otherStreamsInitialized = false

  _joiningSession = false

  joinDialogState = new ManagedDialogState()
  enrollDialogState = new ManagedDialogState()

  @observable paymentUrlLoading = false
  @observable paymentUrl = ''
  @observable _slideDeckAccessDenied = false
  // Retry budget is scoped to a single deck; see resetSlideDeckAccessRetries.
  private slideDeckAccessRetryDeckId?: string
  private slideDeckAccessRetryCount = 0
  private slideDeckAccessRetryCapReported = false
  @observable isRefreshingMaterials = false

  private onSectionFetchPermissionError?: () => void
  // Called after the student deliberately leaves their room. The page wires
  // this to an explicit navigate to the no-room `assignment` route — the ONLY
  // way to reach that route now that the auto-redirect-back was removed.
  private onRoomLeft?: () => void

  constructor(
    repository: FirebaseRepository,
    {
      sectionId,
      assignmentId,
      roomStateId,
      onSectionFetchPermissionError,
      onRoomLeft,
    }: {
      sectionId: string
      assignmentId: string
      roomStateId?: string
      onSectionFetchPermissionError?: () => void
      onRoomLeft?: () => void
    }
  ) {
    super()
    this.repository = repository
    const user = repository.breakoutUser
    assert(user, 'User must be logged in')
    this.user = user

    makeObservable(this)

    this.sectionId = sectionId
    this.assignmentId = assignmentId
    this.roomStateId = roomStateId
    this.onSectionFetchPermissionError = onSectionFetchPermissionError
    this.onRoomLeft = onRoomLeft

    this.section = Section.empty(this.repository)
    this.assignment = SectionAssignment.empty(this.repository)
    this.slideDeck = SlideDeck.empty(this.repository)
    this.roomState = RoomState.empty(this.repository)
    this.slides = SlideModel.emptyCollection(repository)

    this.questions = SlideQuestion.emptyCollection(this.repository)
    this.answers = RoomStateAnswer.emptyCollection(this.repository)
    this._materials = SlideDeckMaterial.emptyCollection(this.repository)
    this._assignments = SectionAssignment.emptyCollection(this.repository)
    this._slideDecks = SlideDeck.emptyCollection(this.repository)
    this.roles = SlideDeckRole.emptyCollection(this.repository)

    this.roomStateSummary = RoomStateSummary.empty(repository)
    this.roomStates = RoomState.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
    this.sectionPass = UserProfileSectionPass.empty(repository)

    // clear cubit managed dialogs on dispose (if active)
    this.disposers.push(() => {
      this.joinDialogState.clearSelf?.()
      this.enrollDialogState.clearSelf?.()
      this._peerReviewController?.dispose()
      this.chatController?.dispose()
    })
  }

  initialize(): void {
    this.addStream(
      getSection(this.repository, { sectionId: this.sectionId }),
      (section) => {
        if (!section.exists) {
          this.onSectionFetchPermissionError?.()
          return
        }

        this.section.replaceModel(section)

        const sectionPassKey = 'section-pass-pricing'

        // Only build the downstream streams/controllers once. The section doc
        // is touched (updatedAt bumped) whenever the instructor edits the
        // assignment (e.g. group size or dates), which re-fires this callback.
        // Rebuilding everything on each update tears down and re-subscribes the
        // sub-streams and controllers, producing a visible loading flicker for
        // the student. Running this once lets the persistent assignment stream
        // deliver instructor edits live and smoothly.
        if (!this._otherStreamsInitialized) {
          this._otherStreamsInitialized = true
          this.initializeOtherStreams()
        }

        if (!this.section.usesSectionPass || this.hasStream(sectionPassKey))
          return

        this.addStream(
          getSettingsSectionPassPricing(this.repository),
          (pricing) => {
            this.sectionPassPricing.replaceModel(pricing)
          },
          {
            name: sectionPassKey,
          }
        )

        this.addStream(
          getSectionPassForSection(this.repository, {
            userId: this.repository.uid,
            sectionId: this.sectionId,
          }),
          (pass) => {
            this.sectionPass.replaceModel(pass)

            const hasSectionPass = pass.hasData

            if (!hasSectionPass) {
              fetchSectionAssignments(this.repository, {
                sectionId: this.sectionId,
                onlyPublished: true,
              }).then((assignments) => {
                this._assignments.replaceModels(assignments)

                const slideDeckIds = assignments.map((a) => a.data.slideDeckId)

                if (slideDeckIds.length > 0) {
                  fetchSlideDecksById(this.repository, {
                    slideDeckIds,
                  }).then((slideDecks) => {
                    this._slideDecks.replaceModels(slideDecks)
                  })
                }
              })
            }
          }
        )
      },
      {
        disableCaptureException: true,
        name: 'section',
        onError: (error) => {
          if (
            this.onSectionFetchPermissionError &&
            'code' in error &&
            error.code === 'permission-denied'
          ) {
            captureException(
              new UnauthorizedStudentAssignmentAccessError(
                'Permission denied when fetching section'
              ),
              {
                extra: {
                  sectionId: this.sectionId,
                  assignmentId: this.assignmentId,
                },
              }
            )

            this.onSectionFetchPermissionError()
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  initializeOtherStreams() {
    this.addStream(
      getSectionAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (assignment) => {
        this.assignment.replaceModel(assignment)

        // v1 reads the deck doc from Firestore; v2 gets the deck from the same
        // Adonis response as its subcollections (see fetchSlideDeckDataViaApi),
        // so skip the redundant Firestore read there.
        if (!this.repository.readsSlideDeckFromV2) {
          this.fetchSlideDeck(assignment.data.slideDeckId)
        }
        this.fetchSlideDeckDataViaApi(
          assignment.data.slideDeckId,
          'assignment-stream'
        )
      },
      {
        name: 'assignment',
      }
    )

    // Re-fetch slide deck data when access state changes (e.g. student
    // purchases a section pass after being denied). The assignment stream
    // won't re-fire since the assignment doc doesn't change on purchase.
    this.disposers.push(
      reaction(
        () => ({
          denied: this._slideDeckAccessDenied,
          hasPass: this.user.sectionPassSectionIds.has(this.sectionId),
          slideDeckId: this.assignment.data.slideDeckId,
        }),
        ({ denied, hasPass, slideDeckId }) => {
          if (!denied || !hasPass || !slideDeckId) return

          // An assignment that swaps decks gets a fresh budget rather than
          // inheriting the previous deck's exhausted count.
          if (this.slideDeckAccessRetryDeckId !== slideDeckId) {
            this.resetSlideDeckAccessRetries(slideDeckId)
          }

          if (this.slideDeckAccessRetryCount >= MAX_SLIDE_DECK_ACCESS_RETRIES) {
            // The student is stuck on a denied deck. Report the state mismatch
            // once per deck so the underlying cause stays visible.
            if (!this.slideDeckAccessRetryCapReported) {
              this.slideDeckAccessRetryCapReported = true
              captureException(
                new Error('Slide deck access retry cap reached'),
                {
                  extra: {
                    sectionId: this.sectionId,
                    assignmentId: this.assignmentId,
                    slideDeckId,
                    hasPass,
                  },
                }
              )
            }
            return
          }

          this.slideDeckAccessRetryCount += 1
          runInAction(() => {
            this._slideDeckAccessDenied = false
          })
          this.fetchSlideDeckDataViaApi(slideDeckId, 'access-denied-recovery')
        }
      )
    )

    this.addStream(
      getRoomStates(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (roomStates) => {
        this.roomStates.replaceModels(roomStates)
        if (this.roomStateId) {
          // A room id in the URL that is gone from this assignment (deleted /
          // never existed / foreign) OR one the student can't view => show the
          // no-access screen and NEVER auto-redirect. We keep roomStateId so the
          // router does not move; the only way out is the pane's button, an
          // explicit user action. Auto-redirecting here (clearing + jumping to
          // another room) was the getSlideDeck remount ring.
          const room = this.roomStates._models.find(
            (room) => room.id === this.roomStateId
          )
          this.setRoomAccessDenied(
            !(room && this.userCanViewRoom(room, this.user))
          )
          return
        }
        // No room in the URL (the plain assignment entry, not a bad room id):
        // whenever the URL has no room and the student is a member of one,
        // resume it. This runs on every snapshot, not just first load — it's
        // also what snaps a still-grouped student back in after they click the
        // no-access pane's "Back to assignment" button.
        // (No setRoomAccessDenied(false) needed: roomStateId is only ever
        // cleared via changeRoomStateId, which already resets the flag.)
        const roomId = this.getCurrentUserRoomId()
        if (roomId !== this.roomStateId) {
          this.changeRoomStateId(roomId)
        }
      }
    )

    this.restartRoomStateStream()
    this.restartUserAnswersStream()
    this.restartRoomStateSummaryStream()
    this.restartChatController()
    this.restartRoomGradedStream()
    this.restartStudentGradedStream()
    this.restartPeerReviewController()

    this.addReaction({
      // Also react to roomAccessDenied so the per-room streams tear down the
      // moment a room becomes inaccessible (and re-subscribe if it becomes
      // viewable again), not just on a roomStateId change.
      whenThisChanges: () => [this.roomStateId, this.roomAccessDenied],
      thenRunThisCode: () => {
        this.restartRoomStateStream()
        this.restartUserAnswersStream()
        this.restartRoomStateSummaryStream()
        this.restartChatController()
        this.restartRoomGradedStream()
        this.restartStudentGradedStream()
        this.restartPeerReviewController()
      },
    })

    this.addReaction({
      whenThisChanges: () => this.userIsInCurrentRoom,
      thenRunThisCode: () => {
        this.restartUserAnswersStream()
      },
    })

    this.addReaction({
      whenThisChanges: () => this.roomState.userIds,
      thenRunThisCode: (userIds) => {
        // check if list is same, if different list, replace
        if (
          userIds.length !== this._roomStateUserIds.length ||
          userIds.some((id, index) => id !== this._roomStateUserIds[index])
        ) {
          this._roomStateUserIds.replace(userIds)
        }
      },
    })
  }

  @action
  changeRoomStateId(roomStateId: string | undefined) {
    this.roomStateId = roomStateId
    // A deliberate room change (join, leave, click-through, first-load pick)
    // starts a fresh access evaluation.
    this.roomAccessDenied = false
  }

  @action
  setRoomAccessDenied(denied: boolean) {
    this.roomAccessDenied = denied
  }

  getCurrentUserRoomId() {
    return this.roomStates._models.find((room) =>
      room.userIds.includes(this.repository.uid)
    )?.id
  }

  restartStudentGradedStream(retryCount: number = 0) {
    const key = 'student-graded'
    if (this.hasStream(key)) this.removeStream(key)
    if (!this.roomStateId || this.roomAccessDenied) return
    if (!this.repository.uid) return

    this.addStream(
      getStudentWasGraded(this.repository, {
        roomStateId: this.roomStateId,
        userId: this.repository.uid,
      }),
      (isGraded) => {
        runInAction(() => {
          this._studentGraded.set(isGraded)
        })
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartStudentGradedStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartRoomGradedStream(retryCount: number = 0) {
    const key = 'room-graded'
    if (this.hasStream(key)) this.removeStream(key)
    if (!this.roomStateId || this.roomAccessDenied) return

    this.addStream(
      getRoomWasGraded(this.repository, { roomStateId: this.roomStateId }),
      (roomGraded) => {
        runInAction(() => {
          this._roomGraded.set(roomGraded)
        })
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomGradedStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartRoomStateStream(retryCount: number = 0): void {
    const key = 'room-state'
    if (this.hasStream(key)) this.removeStream(key)

    if (!this.roomStateId || this.roomAccessDenied) {
      this.roomState.replaceModel(RoomState.empty(this.repository))
      return
    }

    this.addStream(
      getRoomState(this.repository, { roomStateId: this.roomStateId }),
      (roomState) => {
        // Access (including being removed from the room) is decided in one place
        // — the getRoomStates resolver via userCanViewRoom, which honors
        // hiddenUserIds. We deliberately don't clear/redirect or set the
        // no-access flag from this per-room stream: two authorities keying off
        // different membership rules (userIds here vs userIds+hiddenUserIds
        // there) fought over the state and drove the remount ring.
        if (this.roomStateId !== roomState.id) return
        // A room the URL points at can be gone (deleted / never existed) while
        // we keep roomStateId to show the no-access pane. A non-existent doc
        // streams as a model with no data, so fall back to an empty model to
        // keep roomState.data.* reads safe for the computeds that run anyway.
        this.roomState.replaceModel(
          roomState.hasData ? roomState : RoomState.empty(this.repository)
        )
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          this.roomStateSummary.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomStateStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  restartChatController() {
    if (!this.roomStateId || this.roomAccessDenied) {
      this.chatController?.dispose()
      this.chatController = undefined
      return
    }
    this.chatController = new StudentAssignmentChatController({
      repository: this.repository,
      roomId: this.roomStateId,
    })

    this.chatController.initialize()
  }

  restartPeerReviewController() {
    if (!this.roomStateId || this.roomAccessDenied) {
      this._peerReviewController?.dispose()
      this._peerReviewController = undefined
      return
    }
    this._peerReviewController = new StudentPeerReviewController(
      this.repository,
      {
        roomId: this.roomStateId,
        userId: this.repository.uid,
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        peerReviewExcludesSelfReview:
          this.assignment.data.peerReviewExcludesSelfReview ?? false,
        userIdsInRoom: this.roomState.userIds,
      }
    )
    this._peerReviewController.initializeAsReviewer()
    return this._peerReviewController as StudentPeerReviewController
  }

  restartRoomStateSummaryStream(retryCount: number = 0) {
    const key = 'summary'
    if (this.hasStream(key)) this.removeStream(key)

    if (!this.roomStateId || this.roomAccessDenied) {
      this.roomState.replaceModel(RoomState.empty(this.repository))
      return
    }

    this.addStream(
      getRoomStateSummary(this.libraryObject.repository, {
        roomId: this.roomStateId,
      }),
      (summary) => {
        this.roomStateSummary.replaceModel(summary)
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          this.roomStateSummary.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartRoomStateSummaryStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  /**
   * The current student's hard deadline as a DateTime, honoring their
   * individual deadline extension. Used as the max bound when scheduling a
   * room so an extended student can schedule past the section-wide deadline.
   */
  @computed
  get effectiveExpiresAtDate(): DateTime | undefined {
    const date = this.assignment.getEffectiveExpiresAt(
      this.repository.uid ?? ''
    )
    return date ? DateTime.fromJSDate(date) : undefined
  }

  /**
   * Whether the assignment is not yet available to the current student,
   * honoring their individual availableFrom override. Use this instead of
   * `assignment.assignedAtIsInFuture` for student-facing "not yet available"
   * gating so an extended student's earlier/later open date is respected.
   */
  @computed
  get assignedAtIsInFutureForCurrentUser(): boolean {
    return this.assignment.assignedAtIsInFutureForUser(
      this.repository.uid ?? ''
    )
  }

  /**
   * The open date paired with assignedAtIsInFutureForCurrentUser: the current
   * student's effective availableFrom (their individual override when
   * present). Surfaces that gate on the boolean must display THIS date, never
   * the assignment-level assignedAtDate — otherwise a student with a future
   * individual availableFrom is told the assignment opens on a base date
   * that may already be in the past.
   */
  @computed
  get effectiveAssignedAtDate(): DateTime | undefined {
    const date = this.assignment.getEffectiveAssignedAt(
      this.repository.uid ?? ''
    )
    return date ? DateTime.fromJSDate(date) : undefined
  }

  /** Earliest a room can be scheduled for the current student (honors their availableFrom). */
  @computed
  get effectiveMinScheduleDate(): DateTime {
    const now = this.repository.currentMinute
    const assignedAt = this.assignment.getEffectiveAssignedAt(
      this.repository.uid ?? ''
    )
    return assignedAt
      ? DateTime.max(now, DateTime.fromJSDate(assignedAt).startOf('minute'))
      : now
  }

  @computed
  /**
   * for section pass return the price after rebate in USD
   * for token payment method return price as `token quantity`
   */
  get assignmentPurchasePrice() {
    const { sectionPassPriceCents } = this.libraryObject
    const { slideDeckPrice } = this.slideDeck.data

    const useSectionPassPricing =
      this.section.usesSectionPass &&
      this.sectionPassPricing.sectionPassPriceInt > 0

    const { costAfterRebate, rebateCentsToApply } =
      getSectionPassPriceAfterRebate({
        sectionPassPriceCents: sectionPassPriceCents,
        totalRebateAmountCents: this.user.sectionPassRebateAvailableCents,
      })

    const formatCentsToDollarsStr = (cents: number) => {
      const dollarsStr = (cents / 100).toFixed(2)
      return dollarsStr.endsWith('.00')
        ? dollarsStr.substring(0, dollarsStr.length - 3)
        : dollarsStr
    }

    if (useSectionPassPricing)
      return {
        unitType: 'cent' as const,
        amount: costAfterRebate,
        amountStr: formatCentsToDollarsStr(costAfterRebate),
        rebateAmountToApplyDollarStr:
          rebateCentsToApply > 0
            ? formatCentsToDollarsStr(rebateCentsToApply)
            : null,
        totalRebateAmountDollarStr: formatCentsToDollarsStr(
          this.user.sectionPassRebateAvailableCents
        ),
      }

    return {
      unitType: 'token' as const,
      amount: slideDeckPrice,
      amountStr: slideDeckPrice.toString(),
    }
  }

  @computed get targetGroupNumber() {
    // expected students and max group size should always be at least one to prevent division errors
    const expectedStudents = Math.max(
      this.section.data.expectedStudents ?? 0,
      this.section.userCount,
      1
    )
    const maxGroupSize = Math.max(
      this.assignment.data.groupingSizeMaximum ?? RoomState.maxAllowedUsers,
      1
    )
    return Math.ceil(expectedStudents / maxGroupSize)
  }

  @computed get chat() {
    return (
      this.chatController ??
      new StudentAssignmentChatController({
        repository: this.repository,
        roomId: this.roomStateId || '',
      })
    )
  }

  @computed
  get peerReview() {
    if (this._peerReviewController) return this._peerReviewController
    return this.restartPeerReviewController()
  }

  @computed
  get estimatedExperienceDurationInMinutes(): number {
    const durationInMinutes = (slide: SlideModel) => {
      let duration = 0
      if (slide.data.slideVideoDuration) {
        duration = Math.ceil(slide.data.slideVideoDuration / 60)
      } else {
        duration = Math.ceil(slide.data.slideDuration / 60)
      }
      return duration
    }

    return calculateTimeLeftInSession({
      slides: this.slides._models.map((slide) => ({
        durationInMinutes: durationInMinutes(slide),
      })),
      roundToNearest: 5,
    })
  }

  // Hands `slideDeckId` a full retry budget. Called when the deck changes and
  // after a successful fetch, so the cap only ever bounds consecutive denials
  // for one deck.
  private resetSlideDeckAccessRetries(slideDeckId: string) {
    this.slideDeckAccessRetryDeckId = slideDeckId
    this.slideDeckAccessRetryCount = 0
    this.slideDeckAccessRetryCapReported = false
  }

  @observable private slideDeckDataApiFetched?: string
  async fetchSlideDeckDataViaApi(slideDeckId: string, reason: string) {
    if (this.slideDeckDataApiFetched === slideDeckId) return
    runInAction(() => {
      this.slideDeckDataApiFetched = slideDeckId
    })

    const include: SlideDeckInclude[] = [
      'questions',
      'slides',
      'materials',
      'roles',
    ]

    try {
      // The repository picks the read source (v1 cloud function vs v2 Adonis)
      // from the global override — we only note which landed so we can populate
      // the deck from a v2 response instead of a separate Firestore read.
      const useV2 = this.repository.readsSlideDeckFromV2

      const result = await this.repository.fetchSlideDeckForAssignment({
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        slideDeckId,
        include,
        callsite: 'StudentAssignmentCubit.fetchSlideDeckDataViaApi',
        reason,
        instanceId: this.instanceId,
        // Only include when set; the callable payload must not carry undefined
        // (solo/no-room assignments have no roomStateId).
        ...(this.roomStateId && { clientRoomStateId: this.roomStateId }),
      })

      if (!result.success) {
        // A `{ success: false }` comes back as a normal HTTP 200, so it never
        // hits the catch below and used to be eaten. Report the unexpected
        // failures (NOT_FOUND / NOT_MEMBER / INTERNAL_ERROR) so they surface.
        // ACCESS_DENIED is the normal paywall flow for an unpaid student on a
        // non-free deck, so skip it — capturing it would bury the real cases.
        if (result.errorCode !== 'ACCESS_DENIED') {
          captureException(
            new Error(
              `getSlideDeckForAssignment returned success:false: ${result.error}`
            ),
            {
              tags: { feature: 'slide-deck-api', errorCode: result.errorCode },
              extra: {
                sectionId: this.sectionId,
                assignmentId: this.assignmentId,
                slideDeckId,
              },
            }
          )
        }
        runInAction(() => {
          // v2 skips the Firestore deck read, so the deck only arrives with the
          // API response. On a payment denial the endpoint still returns it —
          // populate it so the page becomes ready and shows the paywall instead
          // of an infinite spinner.
          if (useV2 && result.slideDeck) {
            this.slideDeck.replaceModel(
              SlideDeck.buildModelFromApiResponse(
                this.repository,
                result.slideDeck
              )
            )
            this.slideDeckFetched = slideDeckId
          }
          this._slideDeckAccessDenied = true
          this.slideDeckDataApiFetched = undefined
        })
        return
      }

      runInAction(() => {
        this._slideDeckAccessDenied = false
        this.resetSlideDeckAccessRetries(slideDeckId)

        // v2 returns the deck in the same response — use it instead of a second
        // Firestore read, and mark the deck fetched so nothing re-reads it.
        if (useV2) {
          this.slideDeck.replaceModel(
            SlideDeck.buildModelFromApiResponse(
              this.repository,
              result.slideDeck
            )
          )
          this.slideDeckFetched = slideDeckId
        }

        populateModelsFromApiResponse(this.repository, result, [
          {
            key: 'questions',
            modelClass: SlideQuestion,
            target: this.questions,
          },
          { key: 'slides', modelClass: SlideModel, target: this.slides },
          {
            key: 'materials',
            modelClass: SlideDeckMaterial,
            target: this._materials,
          },
          { key: 'roles', modelClass: SlideDeckRole, target: this.roles },
        ])
      })
    } catch (e) {
      console.error('[fetchSlideDeckDataViaApi] error:', e)
      runInAction(() => {
        this.slideDeckDataApiFetched = undefined
        // Mark collections as loaded (empty) so the page doesn't hang
        this.questions.replaceModels([])
        this.slides.replaceModels([])
        this._materials.replaceModels([])
        this.roles.replaceModels([])
      })
      captureException(e, { tags: { feature: 'slide-deck-api' } })
    }
  }

  private slideDeckFetched?: string
  async fetchSlideDeck(slideDeckId: string) {
    // guard - only fetch the slide deck once
    if (this.slideDeckFetched === slideDeckId) return
    this.slideDeckFetched = slideDeckId

    try {
      const slideDeck = await fetchSlideDeck(this.repository, {
        slideDeckId: slideDeckId,
      })

      this.slideDeck.replaceModel(slideDeck)
    } catch (e) {
      // but if we fail, we should try again next time
      this.slideDeckFetched = undefined
    }
  }

  restartUserAnswersStream(retryCount: number = 0) {
    const key = 'user-answers'
    if (this.hasStream(key)) this.removeStream(key)
    if (
      !this.roomStateId ||
      this.roomAccessDenied ||
      !this.userIsInCurrentRoom
    ) {
      this.answers.replaceModels([])
      this.answers.changeLoadingState(true)
      return
    }
    this.addStream(
      getRoomStateAnswers(this.repository, {
        roomId: this.roomStateId,
        userId: this.repository.uid,
      }),
      (answers) => {
        this.answers.replaceModels(answers)
      },
      {
        name: key,
        disableCaptureException: true,
        onError: (err) => {
          this.answers.changeLoadingState(true)
          if (retryCount < 5) {
            setTimeout(() => {
              this.restartUserAnswersStream(retryCount + 1)
            }, 400 * retryCount)
          } else {
            console.error(err)
            captureException(err)
          }
        },
      }
    )
  }

  async updateRoomVideoMethod(videoMethod: RoomStateVideoMethod) {
    if (!this.roomStateId) return

    return updateRoomState(this.repository.firestore, this.roomStateId, {
      videoMethod,
    })
  }

  async leaveRoom(): Promise<void> {
    if (!this.roomStateId) return Promise.resolve()

    const roomStateId = this.roomStateId

    await leaveRoom(this.repository.firestore, {
      roomId: this.roomStateId,
      userId: this.repository.uid,
    })

    this.logEvent('room_leave', { room_id: roomStateId })

    this.changeRoomStateId(undefined)
    // Explicit navigation for a user-initiated leave. The reaction in the page
    // no longer redirects to the no-room route, so this is what updates the URL.
    this.onRoomLeft?.()
  }

  async joinRoom(
    roomId: string,
    { isGroupLeader = false }: { isGroupLeader: boolean }
  ): Promise<void> {
    await roomStateAddUser(this.repository.firestore, {
      roomId,
      userId: this.repository.uid,
      isGroupLeader: isGroupLeader,
    })
    this.logEvent('room_state_joined', { room_id: roomId })
    this.changeRoomStateId(roomId)
  }

  async updateRoomScheduledTime(scheduledAt: DateTime | undefined) {
    if (!this.roomStateId) throw new Error('No room state')

    await updateRoomState(this.repository.firestore, this.roomStateId, {
      scheduledAt: scheduledAt ? scheduledAt.toJSDate() : deleteField(),
    })
  }

  /**
   * include roomId argument to target room that is not the current room
   */
  async updateRoom(newName: string, newDateTime: DateTime | undefined) {
    if (!this.roomStateId) {
      const instructorId = this.section.data.instructorUserId

      const persist =
        !!this.repository.breakoutUser?.isCorre ||
        instructorId === this.repository.currentUser?.uid ||
        !!this.repository.breakoutUser?.isAssistantOfInstructor(instructorId)

      const newRoomState = await createRoomState(this.repository.firestore, {
        userId: this.repository.uid,
        scheduled: newDateTime ? newDateTime.toJSDate() : undefined,
        name: newName,
        assignmentId: this.assignmentId,
        sectionId: this.sectionId,
        slideDeckId: this.slideDeck.id,
        deletable: !persist,
      })

      this.changeRoomStateId(newRoomState.id)
      this.logEvent('room_state_created', { room_id: newRoomState.id })
    } else {
      this.logEvent('room_state_updated', { room_id: this.roomStateId })
      if (!this.roomState.data.scheduledAt && newDateTime) {
        this.logEvent('room_schedule', { room_id: this.roomStateId })
      }
      return updateRoomState(this.repository.firestore, this.roomStateId, {
        roomStateName: newName,
        scheduledAt: newDateTime ? newDateTime.toJSDate() : deleteField(),
      })
    }
  }

  private _soloRoomCreation?: Promise<void>

  /**
   * Solo assignments skip grouping; the 1-person room is created lazily on the
   * first action that needs a roomId (quiz start or Join Session).
   */
  async ensureSoloRoom(): Promise<void> {
    if (!this.assignment.isSolo || this.roomStateId) return
    // instructor-led solo: rules deny student room creation; the instructor
    // places each participant into their own room
    if (this.assignment.data.forbidStudentGroupCreation) return
    // memoized so concurrent calls can't create two rooms; cleared once
    // settled so a failure can be retried and a room deleted later (e.g. by
    // an instructor) can be recreated
    this._soloRoomCreation ??= (async () => {
      // a real name keeps the room visible to screen readers (card
      // aria-label) and to name-keyed exports like the quiz results CSV
      const [name] = generateUniqueGroupNames(
        this.repository,
        1,
        this.roomStates.models.map((room) => room.data.roomStateName || '')
      )
      await this.updateRoom(name, undefined)
    })().finally(() => {
      this._soloRoomCreation = undefined
    })
    await this._soloRoomCreation
  }

  async startSoloSession(): Promise<void> {
    if (!this.assignment.isSolo) return
    await this.ensureSoloRoom()
    if (!this.roomStateId) return
    // fill the meeting time only when blank; skipped when participant
    // scheduling is forbidden (rules would deny the write)
    if (
      !this.roomState.isScheduled &&
      !this.assignment.data.forbidStudentGroupScheduling
    ) {
      await this.updateRoomScheduledTime(DateTime.now())
    }
  }

  async answerQuestion(payload: {
    questionId: string
    answerId?: string
    answer: number
    answerList?: number[]
  }) {
    if (!this.roomStateId) return Promise.resolve()

    this.logEvent('room_answer_question', {
      slide_question_id: payload.questionId,
    })

    return upsertUserAnswer(this.repository, payload.questionId, {
      userId: this.repository.uid,
      roomId: this.roomStateId,
      assignmentId: this.assignmentId,
      sectionId: this.sectionId,
      answer: payload.answer,
      answerId: payload.answerId || '',
      answerList: payload.answerList || [],
      isGroupAnswer: false,
    })
  }

  async submitAnswer(payload: { answerId: string }) {
    if (!this.roomStateId) return

    this.logEvent('room_answer_submitted', {
      answerId: payload.answerId,
    })

    submitUserAnswer(this.repository.firestore, {
      roomId: this.roomStateId,
      userId: this.repository.uid,
      answerId: payload.answerId,
      isGroupAnswer: false,
    })
  }

  async startMeeting() {
    const roomStateId = this.roomStateId
    if (!roomStateId) return

    this.roomState.startRoomIfNotStarted()

    this.logEvent('room_start', { room_id: roomStateId })
  }

  @computed
  get currentUserCanManageAssignment() {
    const user = this.repository.breakoutUser
    if (!user) return false
    return (
      user.isCorre ||
      this.currentUserIsInstructor ||
      user.isAssistantOfInstructor(this.section.data.instructorUserId)
    )
  }

  @computed
  get currentUserCanScheduleRoom() {
    if (this.currentUserCanManageAssignment) return true

    // Now, any user who is in the room can schedule the room
    return this.userIsInCurrentRoom
  }

  @computed
  get userIsInCurrentRoom() {
    return this.roomState.userIds.includes(this.repository.uid)
  }

  @computed
  get userRoomStates(): RoomState[] {
    return this.roomStates.models.filter((room) =>
      room.userIds.includes(this.repository.uid)
    )
  }

  @computed
  get hasMultipleRooms(): boolean {
    return this.userRoomStates.length > 1
  }

  @computed
  get switchableRoomStates(): RoomState[] {
    return this.userRoomStates.filter((room) => room.id !== this.roomStateId)
  }

  /**
   * arrays replacement can trigger updates when the content hasn't meaningfully changed
   * use this as a stable reference to the user ids in the room state where we
   * only want to react to real userId changes as opposed to all room state updates
   */
  @computed
  get roomStateUserIds() {
    return this._roomStateUserIds
  }

  @computed
  get materials() {
    return this._materials
  }

  /**
   * Re-fetch materials from the API to get fresh signed URLs.
   * Call this when a material's signed URL has expired.
   */
  async refreshMaterials() {
    const slideDeckId = this.assignment.data.slideDeckId
    if (!slideDeckId) return

    try {
      const result = await this.repository.fetchSlideDeckForAssignment({
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        slideDeckId,
        include: ['materials'],
        callsite: 'StudentAssignmentCubit.refreshMaterials',
        reason: 'materials-url-refresh',
        instanceId: this.instanceId,
        // Only include when set; the callable payload must not carry undefined
        // (solo/no-room assignments have no roomStateId).
        ...(this.roomStateId && { clientRoomStateId: this.roomStateId }),
      })

      if (result.success) {
        runInAction(() => {
          populateModelsFromApiResponse(this.repository, result, [
            {
              key: 'materials',
              modelClass: SlideDeckMaterial,
              target: this._materials,
            },
          ])
        })
      } else if (result.errorCode !== 'ACCESS_DENIED') {
        // Same as fetchSlideDeckDataViaApi: a { success: false } is HTTP 200 and
        // would otherwise be dropped silently. Report the unexpected codes so a
        // broken signed-URL refresh surfaces; ACCESS_DENIED is the expected
        // paywall flow and is skipped to avoid noise.
        captureException(
          new Error(`refreshMaterials returned success:false: ${result.error}`),
          {
            tags: { feature: 'refresh-materials', errorCode: result.errorCode },
            extra: {
              sectionId: this.sectionId,
              assignmentId: this.assignmentId,
              slideDeckId,
            },
          }
        )
      }
    } catch (e) {
      captureException(e, { tags: { feature: 'refresh-materials' } })
    }
  }

  /**
   * Ensure material URLs are fresh. If any signed URL has expired,
   * re-fetches materials from the API to get new signed URLs.
   * Sets isRefreshingMaterials so the UI can show a loading state.
   */
  async ensureFreshMaterialUrls() {
    const hasExpired = this._materials.models.some((m) => m.isSignedUrlExpired)
    if (hasExpired) {
      runInAction(() => {
        this.isRefreshingMaterials = true
      })
      try {
        await this.refreshMaterials()
      } finally {
        runInAction(() => {
          this.isRefreshingMaterials = false
        })
      }
    }
  }

  /**
   * if no roles, or no issues with current role config, return null
   */
  @computed
  get roleIssueForRoomState() {
    if (this.roles.isLoading || !this.roomState || this.roomState.isLoading)
      return null

    return getRoleIssueForRoomState({
      roomState: this.roomState,
      roles: this.roles.models,
    })
  }

  /**
   * if no minimum group size is set, or the minimum is met, return null
   */
  @computed
  get minGroupSizeIssueForRoomState() {
    if (!this.roomState || this.roomState.isLoading) return null
    if (!this.assignment || this.assignment.isLoading) return null

    return getMinGroupSizeIssue({
      roomState: this.roomState,
      minGroupSize: this.assignment.minGroupSize,
    })
  }

  /**
   * if assignment is not ready to view, or the user does have access,
   * don't make materials available
   * */
  @computed
  get materialsLocked() {
    return (
      !this.libraryObject.hasAccessToSlideDeckContents ||
      this.assignment.assignedAtIsInFutureForUser(this.repository.uid ?? '') ||
      this.assignment.data.assignmentState < 1
    ) //draft or unconfigured
  }

  @computed
  get availableStudentMaterials() {
    // do not show materials until roles are loaded
    if (this.materialsLocked || this._materials.isLoading) return []

    const isCompleted =
      this.libraryObject.libraryObjectState === LibraryObjectState.completed

    // if not student in section, must be TA/instructor/admin viewing, so show all materials
    const shouldFilterOnRole = this.currentUserIsInSection

    const currentRoleAssignment = this.roomState.data.assignmentRoles ?? {}

    const currentRole =
      this.repository.uid in currentRoleAssignment
        ? currentRoleAssignment[this.repository.uid]
        : undefined

    return this._materials.models.filter((material) => {
      // ashold12: idk why we're doing this but i am maintaining existing behavior
      if (isFeaturedImageType(material.data.materialType)) return false

      // do not show post-work until after assignment completion
      if (
        material.data.materialWorkType === SlideDeckMaterialWorkType.postWork &&
        !isCompleted
      )
        return false

      const materialRoles = material.data.assignmentRoles ?? []

      // if not filtering on role
      // or material not assigned to role, show it
      if (!shouldFilterOnRole || !materialRoles.length) return true

      // if material assigned to role, and user is participant in section
      // show only if user has a role that matches material roles
      return currentRole && materialRoles.includes(currentRole)
    })
  }

  @computed
  get roomStateStatus() {
    return this.libraryObject.roomStateStatus
  }

  @computed
  get didRoomStart() {
    return this.roomState.didStart
  }

  @computed
  get hasPreWorkQuiz(): boolean {
    return this.questions.isLoaded && this.preWorkQuestions.length > 0
  }

  @computed
  get isReady(): boolean {
    let extrasLoaded = false // TODO: Name this better?
    if (!this.section.usesSectionPass) {
      extrasLoaded = true
    } else if (this.sectionPass.isLoaded && this.sectionPass.hasData) {
      extrasLoaded = true
    } else if (this._assignments.isLoaded && this._slideDecks.isLoaded) {
      extrasLoaded = true
    }

    return (
      this.section.isLoaded &&
      (!this.section.usesSectionPass || this.sectionPassPricing.isLoaded) &&
      this.assignment.isLoaded &&
      this.slideDeck.isLoaded &&
      this.user.userProfileTokensLoaded &&
      extrasLoaded
    )
  }

  @computed
  get hasQuizData(): boolean {
    if (!this.questions.isLoaded) return false
    // solo students have no room (and thus no answers stream) until the quiz
    // starts; their answer set is genuinely empty, not still loading
    if (this.assignment.isSolo && !this.roomStateId) return true
    return this.answers.isLoaded
  }

  @computed
  get preWorkQuestions(): SlideQuestion[] {
    const validQuestions = this.questions.models.filter(
      (question) => question.isPreMeetingQuestion
    )
    // sorted slide list not required as all questions are on the same slide
    return sortQuestions({ questions: validQuestions, slideIds: [] })
  }

  /**
   * Per-viewer shuffled answer choices, keyed by question id. Not observable
   * and not persisted: it lives for the lifetime of this cubit (one quiz
   * load), so the order stays stable as the student navigates forward and
   * back, and resets only on a full page reload. See BL-4382.
   */
  private shuffledAnswers = new Map<string, ShuffledAnswer[]>()

  /** Returns the stable, per-viewer shuffled answer choices for a
   *  multiple-choice question. See BL-4382. */
  getShuffledAnswers(questionId: string, answers: readonly string[]) {
    return buildShuffledAnswers(this.shuffledAnswers, questionId, answers)
  }

  @computed
  get preWorkAnswersByQuestionId() {
    const answersByQuestionId: Record<string, RoomStateAnswer | undefined> = {}
    const preWorkQuestionIds = this.preWorkQuestions.map((q) => q.id)
    for (const a of this.answers.models) {
      const slideQuestionId = a.data.slideQuestionId
      if (preWorkQuestionIds.includes(slideQuestionId)) {
        answersByQuestionId[a.data.slideQuestionId] = a
      }
    }
    return answersByQuestionId
  }

  @computed
  get allPreWorkQuestionsAnswered(): boolean {
    const answerLookupByQuestionId: Record<string, boolean> = {}
    for (const a of this.answers.models) {
      answerLookupByQuestionId[a.data.slideQuestionId] = !!a.data.submitted
    }

    return this.preWorkQuestions.every(
      (q) => answerLookupByQuestionId[q.id] === true
    )
  }

  @computed
  get preWorkQuizPercentage() {
    const questions = this.preWorkQuestions
    const answersByQuestionId = this.preWorkAnswersByQuestionId
    let questionCount = 0
    let total = 0

    questions.map((question) => {
      questionCount++
      const answer = answersByQuestionId[question.id]

      if (answer) {
        total += getQuizAnswerScore({
          answer,
          question,
        })
      }
    })

    return questionCount === 0 ? 0 : (total / questionCount) * 100
  }

  @computed
  get sortedRoomStateUsers() {
    return this.roomState.users.sort((a, b) => {
      return a.fullName.localeCompare(b.fullName)
    })
  }

  @computed
  get currentUserIsGroupLeader() {
    return this.roomState.data.groupLeaderUserIds.includes(this.repository.uid)
  }

  @computed
  get currentUserIsInSection() {
    return this.section.data.userIds.includes(this.repository.uid)
  }

  @computed
  get currentUserIsInstructor() {
    return this.section.data.instructorUserId === this.repository.uid
  }

  @computed
  get libraryObject() {
    return new ValidLibraryObject({
      repository: this.repository,
      section: this.section,
      assignment: this.assignment,
      slideDeck: this.slideDeck,
      roomState: this.roomState,
      quizData: {
        slideQuestions: this.questions.models,
        userAnswers: this.answers.models,
      },
      slides: this.slides.models,
      sectionPassPricing: this.sectionPassPricing,
      hasGrading: this._roomGraded.get(),
      slideDeckAccessGrantedViaApi:
        !this._slideDeckAccessDenied && !!this.slideDeckDataApiFetched,
    })
  }

  @computed
  get sectionAssignmentsWithSlideDecks() {
    const assignments = this._assignments.models
    const slideDecks = this._slideDecks.models

    const slideDeckMap = new Map(slideDecks.map((deck) => [deck.id, deck]))

    return assignments.map((assignment) => ({
      assignment,
      slideDeck: slideDeckMap.get(assignment.data.slideDeckId),
    }))
  }

  // Dialogs
  /// if the dialog has not popped / is not popped and session started recently
  get canPopJoinDialog() {
    const sessionLive =
      this.libraryObject.libraryObjectState === LibraryObjectState.live

    return (
      !this._joiningSession &&
      sessionLive &&
      this.joinDialogState.canShow &&
      !this.libraryObject.requiresPayment &&
      // don't pop join if user not in room
      this.roomState.userIds.includes(this.repository.uid) &&
      // need below data to be loaded to determine room state status
      this.slideDeck.isLoaded &&
      this.roomState.isLoaded &&
      this.assignment.isLoaded
    )
  }

  get canPopEnrollDialog() {
    return this.libraryObject.requiresPayment && this.enrollDialogState.canShow
  }

  @computed
  get roomGraded() {
    const graded = this._roomGraded.get()
    return {
      value: graded,
      isLoaded: graded !== null,
    }
  }

  @computed
  get studentGraded() {
    const graded = this._studentGraded.get()
    return Boolean(graded)
  }

  setJoiningSession() {
    this._joiningSession = true
  }

  onDialogMountUnmount(
    params:
      | {
          event: 'mount'
          dialogType: 'join' | 'enroll'
          closeSelf: VoidFunction
        }
      | {
          event: 'unmount'
          dialogType: 'join' | 'enroll'
        }
  ) {
    const { event, dialogType } = params

    const manager =
      dialogType === 'join' ? this.joinDialogState : this.enrollDialogState

    if (event === 'mount') return manager.mounted(params.closeSelf)

    manager.unmounted()
  }

  // Payments

  /// create new stripe checkout page and return the sessionId
  /// to the caller
  async createStripeSessionOrConsumeSectionPassRebates(
    quantity: number,
    successUrl: string,
    sectionPassSectionId?: string
  ) {
    const createCheckoutSession = httpsCallable<
      {
        quantity: number
        userId: string
        successUrl: string
        sectionPassSectionId?: string
      },
      string
    >(this.repository.functions, 'stripeCreateCheckoutSessionPayment', {
      timeout: 15 * 1000, // 15 seconds
    })

    try {
      const sessionId = await createCheckoutSession({
        quantity: quantity,
        userId: this.repository.uid,
        successUrl: successUrl,
        sectionPassSectionId: sectionPassSectionId,
        // todo: analytics hookup
        // analyticsClientId: analyticsClientId,
      })
      if (sessionId.data === null) {
        return ''
      }

      return sessionId.data as string
    } catch (e) {
      // The function's code and message go in the Sentry title so the
      // different failures (timeout, already has a pass, ...) can be told
      // apart; the original FirebaseError is attached as the linked cause.
      const reason =
        e instanceof FirebaseError ? `${e.code}: ${e.message}` : String(e)
      throw new StripeCheckoutError(reason, { cause: e })
    }
  }

  /// Creates the Stripe checkout session (or consumes rebates) and returns
  /// what the caller should do next. Navigation is left to the caller so the
  /// web app can pick how to leave an iframe (see `util/iframe`).
  async createPayment({
    quantityToBuy,
    sectionPassSectionId,
    type,
  }:
    | {
        type: 'token'
        quantityToBuy: number
        sectionPassSectionId?: never
      }
    | {
        type: 'sectionPass'
        quantityToBuy?: never
        sectionPassSectionId: string
      }): Promise<
    | { action: 'purchasedSectionPass' }
    | { action: 'redirect'; paymentUrl: string }
  > {
    if (this.libraryObject.slideDeckId === '') {
      throw Error('Slide deck not found.')
    }
    this.paymentUrlLoading = true
    const url = new URL(window.location.href)
    url.searchParams.set('payment', 'success')
    const successUrl = url.toString()
    let paymentUrl: string
    try {
      paymentUrl = await this.createStripeSessionOrConsumeSectionPassRebates(
        type === 'token' ? quantityToBuy : 1, // section pass should always be quantity 1
        successUrl,
        sectionPassSectionId
        //todo: analytics hookup
        //analyticsClientId: getAnalyticsClientId(),
      )
    } finally {
      this.paymentUrlLoading = false
    }

    this.paymentUrl = paymentUrl
    // this.showPurchaseDialog = false;
    if (paymentUrl !== '') {
      // magic string means we consumed rebates
      if (paymentUrl === 'purchasedSectionPass') {
        return { action: 'purchasedSectionPass' }
      }

      return { action: 'redirect', paymentUrl }
      // await _firebaseAnalytics.logBeginCheckout(
      //   value: price / 100,
      //   currency: 'USD',
      //   items: [
      //     AnalyticsEventItem(
      //       itemId: state.sectionAssignment.slideDeckId,
      //       itemName: slideDeck.slideDeckName,
      //       locationId: assignmentId,
      //       quantity: quantityToBuy,
      //       price: price / 100,
      //     ),
      //   ],
      // );
    } else {
      // The function returns null when the caller is not signed in.
      throw new StripeCheckoutError('no checkout session URL returned')
    }
  }

  async redeemSectionPassCoupon(couponId: string) {
    const redeemSectionPassCoupon = httpsCallable<
      {
        sectionId: string
        couponId: string
      },
      boolean | null | undefined | string
    >(this.repository.functions, 'redeemSectionPassCoupon')

    try {
      const { data: success } = await redeemSectionPassCoupon({
        sectionId: this.sectionId,
        couponId,
      })

      // if didn't success return the error msg or false
      if (success !== true) return success ? success : false

      // wait for section pass to exist
      // and tokens to be created via trigger
      await when(
        () =>
          this.user.sectionPassSectionIds.has(this.sectionId) &&
          this.libraryObject.hasAccessToSlideDeckContents,
        { timeout: 15_000 }
      )
      return true
    } catch (e) {
      captureException(e)
      return false
    }
  }

  async enroll() {
    const consumeTokensForAssignment = httpsCallable<
      { sectionId: string; assignmentId: string; roomId: string },
      string
    >(this.repository.functions, 'consumeTokensForAssignment')

    const { data } = await consumeTokensForAssignment({
      assignmentId: this.assignmentId,
      sectionId: this.sectionId,
      roomId: this.roomStateId || '',
    })
    const { message, success } = JSON.parse(data)

    return {
      success,
      message,
    } as { success: boolean; message: string | undefined | null }
  }

  async resetRoomState() {
    if (!this.roomStateId) return
    await roomStateReset(this.repository, { roomId: this.roomStateId })
    this.logEvent('room_reset')
  }

  /**
   *
   * returns **true** if the operation was successful or **false** if already processing
   */
  async startAIProcessing() {
    if (!this.roomStateId) return
    const success = await forceAIProcessing(this.repository, {
      roomId: this.roomStateId,
    })
    if (success) {
      this.logEvent('ai_processing_forced')
    }
    return success
  }

  private userCanViewRoom(roomState: RoomState, user: BreakoutUser) {
    const { userIds, hiddenUserIds } = roomState.data

    if ([...userIds, ...hiddenUserIds].includes(user.uid)) return true

    if (!user.profile.hasData) return false

    if (user.isFaculty || user.isCorre) return true

    return false
  }

  logEvent(name: string, params?: Record<string, unknown>) {
    this.repository.logEvent(name, {
      room_id: this.roomStateId,
      assignment_id: this.assignmentId,
      section_id: this.sectionId,
      slide_deck_id: this.slideDeck.id,
      ...params,
    })
  }
}
